import {useEffect, useRef, useState} from 'react';
import type {GeoCoordinates} from 'src/shared/utils/geoDistance';
import {
  GEO_WARNING_THRESHOLD_FEET,
  haversineDistanceFeet,
} from 'src/shared/utils/geoDistance';
import type {GeoStatus} from './GeoStatusBanner';
import {captureGeoResult} from './geoCapture';

export interface GeoStatusResult {
  status: GeoStatus;
  distanceFeet?: number;
  latitude?: number;
  longitude?: number;
  geoNote?: string;
  isCapturing: boolean;
}

// `site` is typed as fully-numeric `GeoCoordinates`, but an upstream caller could still pass
// a Newtonsoft null — guard defensively rather than let it resolve to a bogus (0,0) fence check.
const hasCoordinates = (site: GeoCoordinates | undefined): site is GeoCoordinates =>
  site != null && site.latitude != null && site.longitude != null;

const resolveStatus = (
  captured: GeoCoordinates | undefined,
  site: GeoCoordinates | undefined
): GeoStatus => {
  if (!hasCoordinates(site)) return 'siteUnlocated';
  if (!captured) return 'noGps';
  return haversineDistanceFeet(captured, site) > GEO_WARNING_THRESHOLD_FEET
    ? 'outOfRange'
    : 'onSite';
};

/**
 * Captures device position once each time `active` flips true, resolved against the site's
 * coordinates rather than re-derived every render. Shared by the completion dialog and Start panel.
 */
export function useGeoStatus(
  active: boolean,
  site: GeoCoordinates | undefined
): GeoStatusResult {
  const [result, setResult] = useState<Omit<
    GeoStatusResult,
    'isCapturing'
  > | null>(null);
  const capturedForRef = useRef(false);

  useEffect(() => {
    if (!active) {
      capturedForRef.current = false;
      setResult(null);
      return;
    }
    if (capturedForRef.current) return;
    capturedForRef.current = true;

    let cancelled = false;
    void captureGeoResult().then(({latitude, longitude, geoNote, coordinates}) => {
      if (cancelled) return;
      setResult({
        status: resolveStatus(coordinates, site),
        distanceFeet:
          coordinates && hasCoordinates(site)
            ? haversineDistanceFeet(coordinates, site)
            : undefined,
        latitude,
        longitude,
        geoNote,
      });
    });

    return () => {
      cancelled = true;
    };
  }, [active, site]);

  return {
    status: result?.status ?? 'siteUnlocated',
    distanceFeet: result?.distanceFeet,
    latitude: result?.latitude,
    longitude: result?.longitude,
    geoNote: result?.geoNote,
    isCapturing: active && !result,
  };
}
